import { scopeProjectRef } from "@t3tools/client-runtime/environment";
import type { EnvironmentId, ProjectId, ScopedProjectRef } from "@t3tools/contracts";
import {
  useComposerDraftStore,
  type DraftId,
  type DraftThreadEnvMode,
} from "../composerDraftStore";
import { requestComposerSubmitOnMount } from "./composerSubmitRequest";

interface ThreadContextLike {
  environmentId: EnvironmentId;
  projectId: ProjectId;
}

interface NewThreadHandler {
  (
    projectRef: ScopedProjectRef,
    options?: {
      branch?: string | null;
      worktreePath?: string | null;
      envMode?: DraftThreadEnvMode;
      startFromOrigin?: boolean;
    },
    // The opened draft's identity, which most callers have no use for.
  ): Promise<unknown>;
}

export interface ChatThreadActionContext {
  readonly activeDraftThread: ThreadContextLike | null;
  readonly activeThread: ThreadContextLike | undefined;
  readonly defaultProjectRef: ScopedProjectRef | null;
  readonly handleNewThread: NewThreadHandler;
}

export function resolveNewDraftStartFromOrigin(input: {
  envMode: DraftThreadEnvMode;
  newWorktreesStartFromOrigin: boolean;
}): boolean {
  return input.envMode === "worktree" && input.newWorktreesStartFromOrigin;
}

export function resolveThreadActionProjectRef(
  context: ChatThreadActionContext,
): ScopedProjectRef | null {
  if (context.activeThread) {
    return scopeProjectRef(context.activeThread.environmentId, context.activeThread.projectId);
  }
  if (context.activeDraftThread) {
    return scopeProjectRef(
      context.activeDraftThread.environmentId,
      context.activeDraftThread.projectId,
    );
  }
  return context.defaultProjectRef;
}

// New threads inherit only the *project* from the current context. Branch,
// worktree, and env mode always come from the user's configured defaults —
// carrying them over from the viewed thread meant "new thread" silently
// reused checkouts and branches. Explicit affordances (branch toolbar's
// "new thread in this worktree") pass those options to handleNewThread
// directly instead.
export async function startNewThreadFromContext(
  context: ChatThreadActionContext,
): Promise<boolean> {
  const projectRef = resolveThreadActionProjectRef(context);
  if (!projectRef) {
    return false;
  }

  await context.handleNewThread(projectRef);
  return true;
}

interface OpenedDraftThread {
  readonly draftId: DraftId;
}

function readOpenedDraftThread(result: unknown): OpenedDraftThread | null {
  if (typeof result !== "object" || result === null || !("draftId" in result)) {
    return null;
  }
  const draftId = (result as { draftId: unknown }).draftId;
  if (typeof draftId !== "string" || draftId.length === 0) {
    return null;
  }
  return { draftId: draftId as DraftId };
}

/** Opens a new draft thread with the prompt prefilled and submits on the chat surface. */
export async function startNewThreadWithPrompt(
  context: ChatThreadActionContext,
  prompt: string,
): Promise<boolean> {
  const trimmed = prompt.trim();
  if (!trimmed) {
    return false;
  }
  const projectRef = resolveThreadActionProjectRef(context);
  if (!projectRef) {
    return false;
  }
  const opened = readOpenedDraftThread(await context.handleNewThread(projectRef));
  if (!opened) {
    return false;
  }
  useComposerDraftStore.getState().setPrompt(opened.draftId, trimmed);
  requestComposerSubmitOnMount(opened.draftId);
  return true;
}
